// Retry-attempt detail (task [3.1], ws-i8up7) — one retry record, expanded,
// composed on the shared ItemDetailPage primitive (header + metadata grid +
// optional alert + code panel). It reuses the SAME useRetryProcessor() fetcher
// as the list screen (no new network seam): the list already loads the full
// `attempts` array in memory, so the detail just looks the row up by :id.
import { ArrowLeft, RefreshCcw } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router';

import { useRetryProcessor } from '@/api/retry-processor/hooks';
import type { RetryAttempt } from '@/api/retry-processor/mock';
import {
	ItemDetailPage,
	type ItemDetailAlert,
	type ItemDetailMetadata,
} from '@/components/layout/item-detail-page';
import { PageContainer } from '@/components/layout/page-container';
import { StatusChip } from '../_components/status-chip';

function formatFullTimestamp(iso: string | null): string {
	if (!iso) return '—';
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? '—'
		: d.toLocaleString(undefined, {
				year: 'numeric',
				month: 'short',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
			});
}

/** Danger/info banner keyed on the retry lifecycle, else no alert. */
function buildAlert(attempt: RetryAttempt): ItemDetailAlert | undefined {
	if (attempt.status === 'exhausted') {
		return {
			tone: 'danger',
			message: `Retries exhausted after ${attempt.attempt} of ${attempt.maxAttempts} attempts. This delivery will not be retried again.`,
		};
	}
	if (attempt.status === 'retrying') {
		return {
			tone: 'info',
			message: `Retry in progress — attempt ${attempt.attempt} of ${attempt.maxAttempts}. Next retry scheduled for ${formatFullTimestamp(attempt.nextRetry)}.`,
		};
	}
	return undefined;
}

function BackToProcessor() {
	return (
		<Link
			to="/retry-processor"
			viewTransition
			className="inline-flex items-center gap-1.5 border border-border-strong px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
		>
			<ArrowLeft className="size-4" aria-hidden="true" />
			Back to processor
		</Link>
	);
}

export default function RetryProcessorDetailPage() {
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	// The list hands the clicked row across the navigation so the drill-down is
	// stable in mock mode: getRetryProcessorMock() reseeds a fresh random set on
	// every fetch, so a background refetch would otherwise reshuffle every id out
	// from under a hook-only lookup. This adds no fetcher — it reuses the row the
	// list already had. The hook lookup below stays the source of truth for a
	// direct/refreshed navigation (no state), and drives loading/error/not-found.
	const passedAttempt = (location.state as { attempt?: RetryAttempt } | null)
		?.attempt;
	const { data, isLoading, isError, refetch } = useRetryProcessor();

	const attempt = passedAttempt ?? data?.attempts.find((a) => a.id === id);

	if (!attempt && isLoading) {
		return (
			<PageContainer>
				<div className="h-10 w-64 animate-pulse rounded-md bg-muted" />
				<div className="grid grid-cols-2 gap-4 md:grid-cols-3">
					{Array.from({ length: 6 }).map((_, i) => (
						<div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
					))}
				</div>
				<div className="h-40 animate-pulse rounded-lg bg-muted" />
			</PageContainer>
		);
	}

	if (!attempt && isError) {
		return (
			<PageContainer>
				<div className="flex flex-col items-center gap-4 py-16 text-center">
					<p className="text-sm text-muted-foreground">
						Couldn’t load the retry processor.
					</p>
					<button
						type="button"
						onClick={() => void refetch()}
						className="border border-border bg-secondary px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
					>
						Try again
					</button>
				</div>
			</PageContainer>
		);
	}

	// Not found — bad/expired :id (or a direct nav where the mock reseeded).
	if (!attempt) {
		return (
			<PageContainer>
				<div className="flex flex-col items-center gap-4 py-16 text-center">
					<div className="flex size-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
						<RefreshCcw className="size-5" aria-hidden="true" />
					</div>
					<div>
						<h1 className="font-serif text-xl font-bold text-foreground">
							Retry attempt not found
						</h1>
						<p className="mt-1 text-sm text-muted-foreground">
							No retry attempt matches this id. It may have been rotated out of
							the queue.
						</p>
					</div>
					<BackToProcessor />
				</div>
			</PageContainer>
		);
	}

	const metadata: ItemDetailMetadata[] = [
		{
			label: 'Recipient',
			value: <span className="font-mono">{attempt.recipient}</span>,
		},
		{ label: 'Status', value: <StatusChip status={attempt.status} /> },
		{ label: 'Provider', value: attempt.provider },
		{
			label: 'Attempt',
			value: (
				<span className="font-mono tabular-nums">
					{attempt.attempt}/{attempt.maxAttempts}
				</span>
			),
		},
		{ label: 'Error Reason', value: attempt.errorReason },
		{
			label: 'Last Attempt',
			value: (
				<span className="font-mono tabular-nums" suppressHydrationWarning>
					{formatFullTimestamp(attempt.lastAttempt)}
				</span>
			),
		},
		{
			label: 'Next Retry',
			value: (
				<span className="font-mono tabular-nums" suppressHydrationWarning>
					{formatFullTimestamp(attempt.nextRetry)}
				</span>
			),
		},
	];

	// Plausible raw attempt payload for the code panel — built from the same
	// record so it stays consistent with the metadata grid above it.
	const payload = {
		id: attempt.id,
		recipient: attempt.recipient,
		provider: attempt.provider,
		status: attempt.status,
		attempt: attempt.attempt,
		maxAttempts: attempt.maxAttempts,
		errorReason: attempt.errorReason,
		lastAttempt: attempt.lastAttempt,
		nextRetry: attempt.nextRetry,
	};

	return (
		<ItemDetailPage
			icon={<RefreshCcw aria-hidden="true" />}
			eyebrow="Retry Processor"
			title={attempt.recipient}
			actions={<BackToProcessor />}
			metadata={metadata}
			alert={buildAlert(attempt)}
			codePanel={{
				heading: 'Attempt payload',
				content: JSON.stringify(payload, null, 2),
			}}
		/>
	);
}
